import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Pencil, TrendingUp, TrendingDown, IndianRupee, RefreshCw, Building2, FileText, FileSpreadsheet } from "lucide-react";

const CATS = [
  { v: "power", l: "Power" }, { v: "rent", l: "Rent" }, { v: "salary", l: "Salary" },
  { v: "maintenance", l: "Maintenance" }, { v: "interest", l: "Interest" }, { v: "other", l: "Other" },
];
const CAT_L = Object.fromEntries(CATS.map((c) => [c.v, c.l]));
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA"); };
const lastOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("en-CA"); };
const emptyExp = { factory_id: "shared", category: "power", cost_type: "fixed", amount: "", date: firstOfMonth(), payee: "", note: "", recurring: false };
const emptyInc = { factory_id: "shared", amount: "", date: firstOfMonth(), source: "", note: "" };

export default function Finance() {
  const [frm, setFrm] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [factory, setFactory] = useState("all");
  const [factories, setFactories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [tab, setTab] = useState("expenses");

  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState(emptyExp);
  const [expEdit, setExpEdit] = useState(null);
  const [incOpen, setIncOpen] = useState(false);
  const [incForm, setIncForm] = useState(emptyInc);
  const [incEdit, setIncEdit] = useState(null);
  const [facOpen, setFacOpen] = useState(false);

  const facName = (id) => id === "shared" ? "Shared / Company-wide" : (factories.find((f) => f.id === id)?.name || id);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (frm) p.set("frm", frm);
    if (to) p.set("to", to);
    if (factory !== "all") p.set("factory", factory);
    const qs = p.toString() ? `?${p.toString()}` : "";
    api.get(`/finance/summary${qs}`).then((r) => setSummary(r.data)).catch(() => toast.error("Could not load finance summary"));
    api.get(`/expenses${qs}`).then((r) => setExpenses(r.data)).catch(() => {});
    api.get(`/incomes${qs}`).then((r) => setIncomes(r.data)).catch(() => {});
  }, [frm, to, factory]);

  useEffect(() => { api.get("/factories").then((r) => setFactories(r.data || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const presetMonth = () => { setFrm(firstOfMonth()); setTo(lastOfMonth()); };
  const presetYear = () => { const y = new Date().getFullYear(); setFrm(`${y}-01-01`); setTo(`${y}-12-31`); };
  const presetAll = () => { setFrm(""); setTo(""); };

  // ---- expense CRUD
  const openExpNew = () => { setExpEdit(null); setExpForm({ ...emptyExp, date: frm || firstOfMonth() }); setExpOpen(true); };
  const openExpEdit = (e) => { setExpEdit(e.id); setExpForm({ ...emptyExp, ...e, amount: String(e.amount) }); setExpOpen(true); };
  const saveExp = async () => {
    const body = { ...expForm, amount: num(expForm.amount) };
    try {
      if (expEdit) await api.put(`/expenses/${expEdit}`, body); else await api.post("/expenses", body);
      toast.success("Expense saved"); setExpOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delExp = async (id) => { if (!window.confirm("Delete this expense?")) return; await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); };

  // ---- income CRUD
  const openIncNew = () => { setIncEdit(null); setIncForm({ ...emptyInc, date: frm || firstOfMonth() }); setIncOpen(true); };
  const openIncEdit = (i) => { setIncEdit(i.id); setIncForm({ ...emptyInc, ...i, amount: String(i.amount) }); setIncOpen(true); };
  const saveInc = async () => {
    const body = { ...incForm, amount: num(incForm.amount) };
    try {
      if (incEdit) await api.put(`/incomes/${incEdit}`, body); else await api.post("/incomes", body);
      toast.success("Income saved"); setIncOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delInc = async (id) => { if (!window.confirm("Delete this income entry?")) return; await api.delete(`/incomes/${id}`); toast.success("Deleted"); load(); };

  const copyRecurring = async () => {
    const d = new Date();
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const src = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
    const tgt = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    try {
      const { data } = await api.post("/expenses/copy-recurring", { from_month: src, to_month: tgt });
      toast.success(data.created ? `${data.created} recurring expense(s) copied from ${src} to ${tgt}` : `Nothing to copy from ${src} (or already added)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportFile = async (kind) => {
    const p = new URLSearchParams();
    if (frm) p.set("frm", frm);
    if (to) p.set("to", to);
    if (factory !== "all") p.set("factory", factory);
    try {
      const res = await api.get(`/finance/summary/${kind}?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = kind === "pdf" ? "aranya_pnl.pdf" : "aranya_pnl.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export P&L"); }
  };

  const profit = summary?.profit ?? 0;
  const chartData = (summary?.monthly || []).map((m) => ({ ...m, label: m.month }));

  return (
    <div className="space-y-8">
      <PageHeader title="Finance & P&L" icon={Wallet}
        subtitle="Track factory expenses, interest & earnings to see overall profit or loss."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-2" data-testid="finance-export-pdf" onClick={() => exportFile("pdf")}><FileText className="h-4 w-4" /> PDF</Button>
            <Button variant="outline" className="gap-2" data-testid="finance-export-excel" onClick={() => exportFile("excel")}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
            <Button variant="outline" className="gap-2" data-testid="finance-manage-factories" onClick={() => setFacOpen(true)}><Building2 className="h-4 w-4" /> Factories</Button>
            <Button variant="outline" className="gap-2" data-testid="finance-copy-recurring" onClick={copyRecurring}><RefreshCw className="h-4 w-4" /> Copy recurring → this month</Button>
          </div>
        } />

      {/* Filters */}
      <Card className="rounded-md border-border p-4 shadow-none">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label>
            <Input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="finance-from" className="h-9 w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="finance-to" className="h-9 w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">Factory</Label>
            <Select value={factory} onValueChange={setFactory}>
              <SelectTrigger className="h-9 w-52" data-testid="finance-factory"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All factories</SelectItem>
                {factories.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                <SelectItem value="shared">Shared / Company-wide</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="text-xs" onClick={presetMonth} data-testid="finance-preset-month">This month</Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={presetYear} data-testid="finance-preset-year">This year</Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={presetAll} data-testid="finance-preset-all">All time</Button>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={TrendingUp} label="Total Earnings" value={money(summary?.total_income)} accent="bg-sage/15 text-sage" testid="fin-income" />
        <Kpi icon={TrendingDown} label="Total Expenses" value={money(summary?.total_expense)} accent="bg-destructive/10 text-destructive" testid="fin-expense" />
        <Kpi icon={IndianRupee} label={profit >= 0 ? "Net Profit" : "Net Loss"} value={money(Math.abs(profit))}
          accent={profit >= 0 ? "bg-sage/15 text-sage" : "bg-destructive/10 text-destructive"} testid="fin-profit" />
        <Kpi icon={Wallet} label="Fixed / Variable" value={`${money(summary?.by_type?.fixed)} / ${money(summary?.by_type?.variable)}`}
          accent="bg-primary/10 text-primary" testid="fin-fixedvar" small />
      </div>

      {/* Chart + breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-md border-border p-6 shadow-none lg:col-span-2">
          <h3 className="font-heading text-lg font-bold">Monthly earnings vs expenses</h3>
          <p className="mb-4 text-sm text-muted-foreground">Green above red = profit for that month</p>
          {chartData.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No data in this range.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e2d8" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="income" name="Earnings" fill="#7A8B76" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expense" name="Expenses" fill="#B4553F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="rounded-md border-border p-6 shadow-none">
          <h3 className="mb-3 font-heading text-lg font-bold">Expenses by category</h3>
          {(summary?.by_category || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No expenses yet.</p>
          ) : (
            <div className="space-y-2">
              {summary.by_category.map((c) => {
                const pct = summary.total_expense ? (c.amount / summary.total_expense) * 100 : 0;
                return (
                  <div key={c.category} data-testid={`fin-cat-${c.category}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium">{CAT_L[c.category] || c.category}</span>
                      <span className="font-heading font-bold">{money(c.amount)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Per-factory P&L */}
      {(summary?.by_factory || []).length > 0 && (
        <Card className="rounded-md border-border p-6 shadow-none" data-testid="fin-by-factory">
          <h3 className="mb-3 font-heading text-lg font-bold">Profit / Loss by factory</h3>
          <div className="overflow-x-auto">
            <Table className="min-w-[480px]">
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Factory</TableHead><TableHead className="text-right">Earnings</TableHead>
                <TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">Profit / Loss</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {summary.by_factory.map((f) => (
                  <TableRow key={f.factory_id}>
                    <TableCell className="font-semibold">{f.name}</TableCell>
                    <TableCell className="text-right text-sage">{money(f.income)}</TableCell>
                    <TableCell className="text-right text-destructive">{money(f.expense)}</TableCell>
                    <TableCell className={`text-right font-heading font-bold ${f.profit >= 0 ? "text-sage" : "text-destructive"}`}>{money(f.profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Expenses / Income tabs */}
      <div className="flex gap-2">
        {[["expenses", "Expenses"], ["income", "Earnings"]].map(([v, l]) => (
          <button key={v} data-testid={`fin-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
        <div className="ml-auto">
          {tab === "expenses"
            ? <Button size="sm" className="gap-1" data-testid="add-expense-button" onClick={openExpNew}><Plus className="h-4 w-4" /> Add Expense</Button>
            : <Button size="sm" className="gap-1" data-testid="add-income-button" onClick={openIncNew}><Plus className="h-4 w-4" /> Add Earning</Button>}
        </div>
      </div>

      {tab === "expenses" ? (
        expenses.length === 0 ? <EmptyState icon={TrendingDown} title="No expenses in range" hint="Add power, rent, salary, maintenance, interest or other costs." /> : (
          <Card className="overflow-x-auto rounded-md border-border shadow-none">
            <Table className="min-w-[720px]">
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Factory</TableHead><TableHead>Category</TableHead>
                <TableHead>Type</TableHead><TableHead>Payee</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {expenses.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{e.date}</TableCell>
                    <TableCell className="whitespace-nowrap">{facName(e.factory_id)}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full text-[11px]">{CAT_L[e.category] || e.category}</Badge></TableCell>
                    <TableCell className="capitalize text-muted-foreground">{e.cost_type}{e.recurring && " · ↻"}</TableCell>
                    <TableCell className="text-muted-foreground">{e.payee || "—"}</TableCell>
                    <TableCell className="text-right font-heading font-bold text-destructive">{money(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`expense-edit-${e.id}`} onClick={() => openExpEdit(e)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`expense-delete-${e.id}`} onClick={() => delExp(e.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      ) : (
        incomes.length === 0 ? <EmptyState icon={TrendingUp} title="No earnings in range" hint="Add income entries as you receive payments." /> : (
          <Card className="overflow-x-auto rounded-md border-border shadow-none">
            <Table className="min-w-[560px]">
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Factory</TableHead><TableHead>Source</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {incomes.map((i) => (
                  <TableRow key={i.id} data-testid={`income-row-${i.id}`}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{i.date}</TableCell>
                    <TableCell className="whitespace-nowrap">{facName(i.factory_id)}</TableCell>
                    <TableCell>{i.source || "—"}</TableCell>
                    <TableCell className="text-right font-heading font-bold text-sage">{money(i.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`income-edit-${i.id}`} onClick={() => openIncEdit(i)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`income-delete-${i.id}`} onClick={() => delInc(i.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      )}

      {/* Expense dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="max-w-lg p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading">{expEdit ? "Edit" : "New"} Expense</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Factory</Label>
                <Select value={expForm.factory_id} onValueChange={(v) => setExpForm({ ...expForm, factory_id: v })}>
                  <SelectTrigger data-testid="expense-factory"><SelectValue /></SelectTrigger>
                  <SelectContent>{factories.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}<SelectItem value="shared">Shared / Company-wide</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Date</Label>
                <Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} data-testid="expense-date" /></div>
              <div className="space-y-1"><Label className="text-xs">Category</Label>
                <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Type</Label>
                <Select value={expForm.cost_type} onValueChange={(v) => setExpForm({ ...expForm, cost_type: v })}>
                  <SelectTrigger data-testid="expense-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="variable">Variable</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Amount ₹</Label>
                <Input type="text" inputMode="decimal" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount" /></div>
              <div className="space-y-1"><Label className="text-xs">Payee</Label>
                <Input value={expForm.payee} onChange={(e) => setExpForm({ ...expForm, payee: e.target.value })} data-testid="expense-payee" placeholder="Landlord / EB / staff" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={expForm.recurring} onChange={(e) => setExpForm({ ...expForm, recurring: e.target.checked })} data-testid="expense-recurring" /> Recurring every month (rent, salary, EMI…)</label>
            <div className="space-y-1"><Label className="text-xs">Note</Label>
              <Input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} data-testid="expense-note" /></div>
          </div>
          <DialogFooter><Button data-testid="expense-save" onClick={saveExp} disabled={num(expForm.amount) <= 0}>Save Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income dialog */}
      <Dialog open={incOpen} onOpenChange={setIncOpen}>
        <DialogContent className="max-w-lg p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading">{incEdit ? "Edit" : "New"} Earning</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Factory</Label>
                <Select value={incForm.factory_id} onValueChange={(v) => setIncForm({ ...incForm, factory_id: v })}>
                  <SelectTrigger data-testid="income-factory"><SelectValue /></SelectTrigger>
                  <SelectContent>{factories.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}<SelectItem value="shared">Shared / Company-wide</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Date</Label>
                <Input type="date" value={incForm.date} onChange={(e) => setIncForm({ ...incForm, date: e.target.value })} data-testid="income-date" /></div>
              <div className="space-y-1"><Label className="text-xs">Amount ₹</Label>
                <Input type="text" inputMode="decimal" value={incForm.amount} onChange={(e) => setIncForm({ ...incForm, amount: e.target.value })} data-testid="income-amount" /></div>
              <div className="space-y-1"><Label className="text-xs">Source</Label>
                <Input value={incForm.source} onChange={(e) => setIncForm({ ...incForm, source: e.target.value })} data-testid="income-source" placeholder="Buyer / invoice" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Note</Label>
              <Input value={incForm.note} onChange={(e) => setIncForm({ ...incForm, note: e.target.value })} data-testid="income-note" /></div>
          </div>
          <DialogFooter><Button data-testid="income-save" onClick={saveInc} disabled={num(incForm.amount) <= 0}>Save Earning</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Factories dialog */}
      <FactoriesDialog open={facOpen} onOpenChange={setFacOpen} factories={factories}
        reload={() => api.get("/factories").then((r) => { setFactories(r.data || []); load(); })} />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid, small }) {
  return (
    <Card data-testid={`kpi-${testid}`} className="rounded-md border-border p-5 shadow-none">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
          <p className={`mt-2 font-heading font-bold ${small ? "text-base" : "text-2xl"}`}>{value}</p>
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
    </Card>
  );
}

function FactoriesDialog({ open, onOpenChange, factories, reload }) {
  const [name, setName] = useState("");
  const add = async () => { if (!name.trim()) return; await api.post("/factories", { name: name.trim(), active: true }); setName(""); toast.success("Factory added"); reload(); };
  const rename = async (f, v) => { await api.put(`/factories/${f.id}`, { name: v, active: f.active !== false }); reload(); };
  const del = async (f) => { if (!window.confirm(`Delete ${f.name}? Existing entries keep their factory tag.`)) return; await api.delete(`/factories/${f.id}`); toast.success("Deleted"); reload(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <DialogHeader><DialogTitle className="font-heading">Factories</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {factories.map((f) => (
            <div key={f.id} className="flex items-center gap-2" data-testid={`factory-item-${f.id}`}>
              <Input defaultValue={f.name} onBlur={(e) => e.target.value !== f.name && rename(f, e.target.value)} data-testid={`factory-name-${f.id}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`factory-delete-${f.id}`} onClick={() => del(f)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <Input placeholder="New factory name" value={name} onChange={(e) => setName(e.target.value)} data-testid="factory-new-name" />
            <Button className="gap-1" data-testid="factory-add" onClick={add}><Plus className="h-4 w-4" /> Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
